import { Injectable } from '@angular/core';
import { asciiToHex, AuthService } from 'services/auth.service';
import { LoggingService } from 'services/logging.service';
import { AppEnvironment } from 'environments/environment';
import { UserDb } from 'services/user-db.service';
import { StillClaimedCheck, describeSkippedAuthDenial, judgeAuthDenialAsync } from 'services/coach-presence-helpers';

/**
 * The outcome of one `readClientDocsAsync` call: whether the read got an answer
 * at all, and what it found. The two are separate because an absent document and
 * an unanswered read look identical in the map — see the method's own comment.
 */
export interface ClientDocsRead<T> {
  /** False only when the fetch did not deliver an answer. An absent document is not this. */
  ok: boolean;
  /** The documents that came back, keyed by `_id`. Missing ones are simply absent. */
  docs: Map<string, T>;
}

/**
 * Authenticated cross-client one-off reads for the Coach Dashboard.
 *
 * Modeled on `CoachPresenceService.readPresenceOnce` — same shared building
 * blocks (`asciiToHex` + `AuthService.getFetchFunction()` +
 * `AppEnvironment.pouchConfig.couch_serverurl`). Used to render the
 * dashboard table on `CoachSettingsPage` WITHOUT entering manage mode for
 * each client.
 *
 * Error semantics mirror `readPresenceOnce`:
 *   404 / network blip                  → empty result (next refresh retries)
 *   401 / 403 (auth revoked or denied)  → `LoggingService.trackSilentProblem`
 *                                          so sustained denial surfaces in
 *                                          Sentry. The call still returns an
 *                                          empty result so the UI degrades to
 *                                          "Unknown" rather than crashing.
 *                                          A caller that passes
 *                                          `stillClaimedAsync` is asked first,
 *                                          and a denial for a client no longer
 *                                          claimed is only logged
 *                                          (df-ad81a785446c). So is every
 *                                          denial while staff impersonate
 *                                          the coach (#6663).
 *
 * Coach-impersonation `temporaryDb` is irrelevant here — these are direct
 * authenticated GET/POST requests to remote CouchDB, never via the local
 * PouchDB wrapper.
 */
@Injectable({ providedIn: 'root' })
export class CoachClientDataService {
  constructor(
    private auth: AuthService,
    private logging: LoggingService,
    private userDb: UserDb,
  ) {}

  /**
   * Fetch a known set of docs (by `_id`) from a client's CouchDB in one
   * `_all_docs?include_docs=true` POST.
   *
   * **Answers whether the READ happened separately from what it found** (#4817).
   * The map alone cannot say: a document that is simply not there is omitted from
   * it, and so is every document when the fetch fails — so an empty map means
   * either "this account is empty" or "we never got an answer", and a caller that
   * guesses between them is wrong half the time. That mattered as soon as
   * something needed to report a MISSING document as a problem: reading absence
   * as a failed read hides the very thing it is looking for.
   *
   * `ok: false` therefore means only "this fetch did not deliver an answer".
   * Judging the contents is the caller's job.
   */
  async readClientDocsAsync<T = any>(
    clientUid: string,
    ids: string[],
    stillClaimedAsync?: StillClaimedCheck,
  ): Promise<ClientDocsRead<T>> {
    const empty: ClientDocsRead<T> = { ok: false, docs: new Map<string, T>() };
    // nothing was asked for, so nothing failed — an empty request is answered, not refused
    if (!ids.length) return { ok: true, docs: new Map<string, T>() };

    try {
      const url = this.dbUrl(clientUid) + '_all_docs?include_docs=true';
      const fetchFn = this.auth.getFetchFunction();
      const response = await fetchFn(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keys: ids }),
      });
      if (!response.ok) {
        this.handleNonOk('readClientDocsAsync', clientUid, response.status, stillClaimedAsync);
        return empty;
      }
      const body = await response.json() as { rows?: Array<{ id?: string; doc?: T | null; error?: string }> };
      const docs = new Map<string, T>();
      for (const row of body.rows || []) {
        // rows for missing keys carry { key, error: "not_found" } with no doc.
        // That is an ANSWER, not a failure — hence ok: true below regardless.
        if (row?.id && row.doc) docs.set(row.id, row.doc);
      }
      return { ok: true, docs };
    } catch (err) {
      // network/parse failure surfaces to Sentry so persistent failures
      // (not just one-off blips) are visible. The empty return keeps
      // the dashboard UI degradation behaviour ("Unknown" row).
      console.log(`coach-client-data: readClientDocsAsync failed for ${clientUid}:`, err?.message || err);
      this.logging.trackSilentProblem('coach-client-data: readClientDocsAsync failed', {
        clientUid,
        message: err?.message,
      });
      return empty;
    }
  }

  /**
   * Fetch a client's log entries whose `_id` falls in `[startISODate,
   * endISODate]`. Log IDs are `log/<ISO-utc>-<type>` (see
   * `assureIdForEntry` in `shared/src/data-model/user-log.ts`), which is
   * lexicographically range-safe — so a CouchDB `_all_docs` range query
   * with `log/<startISODate>` as startkey and `log/<endISODate>￰` as
   * endkey returns the full window in one round-trip.
   *
   * `startISODate` and `endISODate` are full ISO timestamps (e.g.
   * `2026-05-06T00:00:00.000Z`); callers compute them from `today_local`.
   */
  async readClientLogRangeAsync<T = any>(
    clientUid: string,
    startISODate: string,
    endISODate: string,
    stillClaimedAsync?: StillClaimedCheck,
  ): Promise<T[]> {
    try {
      // CouchDB `_all_docs` requires keys to be JSON strings, then URL-encoded.
      const startKey = encodeURIComponent(JSON.stringify(`log/${startISODate}`));
      // ￰ is one of the highest BMP characters used as a sentinel
      // upper bound in CouchDB range queries; matches the precedent
      // documented in the PRD.
      const endKey = encodeURIComponent(JSON.stringify(`log/${endISODate}￰`));
      const url = this.dbUrl(clientUid)
        + `_all_docs?include_docs=true&startkey=${startKey}&endkey=${endKey}`;
      const fetchFn = this.auth.getFetchFunction();
      const response = await fetchFn(url, { method: 'GET' });
      if (!response.ok) {
        this.handleNonOk('readClientLogRangeAsync', clientUid, response.status, stillClaimedAsync);
        return [];
      }
      const body = await response.json() as { rows?: Array<{ id?: string; doc?: T | null }> };
      const docs: T[] = [];
      for (const row of body.rows || []) {
        if (row?.doc) docs.push(row.doc);
      }
      return docs;
    } catch (err) {
      // network/parse failure surfaces to Sentry so persistent failures
      // (not just one-off blips) are visible. The empty return keeps
      // the dashboard + detail-page UI degradation behaviour
      // ("Unknown" / "no items").
      console.log(`coach-client-data: readClientLogRangeAsync failed for ${clientUid}:`, err?.message || err);
      this.logging.trackSilentProblem('coach-client-data: readClientLogRangeAsync failed', {
        clientUid,
        message: err?.message,
      });
      return [];
    }
  }

  private dbUrl(clientUid: string): string {
    const remoteDbName = 'userdb-' + asciiToHex(clientUid);
    return AppEnvironment.pouchConfig.couch_serverurl + remoteDbName + '/';
  }

  private handleNonOk(op: string, clientUid: string, status: number, stillClaimedAsync?: StillClaimedCheck): void {
    if (status === 401 || status === 403) {
      // persistent denial for a client the coach still has — coach access was
      // lost. Surface so sustained failures are visible (transient 5xx are
      // next-refresh-fixable). Judged in the background: the check can wait for
      // the initial sync, and the caller's empty result must not wait with it
      void this.reportAuthDenialAsync(op, clientUid, status, stillClaimedAsync);
    } else {
      console.log(`coach-client-data: ${op} for ${clientUid} returned ${status}`);
    }
  }

  /** Report a 401/403 unless it is expected (see `judgeAuthDenialAsync`). Never throws. */
  private async reportAuthDenialAsync(
    op: string,
    clientUid: string,
    status: number,
    stillClaimedAsync?: StillClaimedCheck,
  ): Promise<void> {
    const verdict = await judgeAuthDenialAsync(this.userDb.isImpersonating(), stillClaimedAsync);
    if (!verdict.report) {
      // staff impersonating the coach, or a client the coach has removed: the server is right to refuse
      console.log(`coach-client-data: ${op} for ${clientUid} denied (${status}); ${describeSkippedAuthDenial(verdict.skipReason)}`);
      return;
    }
    this.logging.trackSilentProblem(`coach-client-data: ${op} auth denied`, {
      clientUid,
      status,
      ...(verdict.checkError ? { stillClaimedCheckError: verdict.checkError } : {}),
    });
  }
}
